import * as React from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CircularProgress, 
  useTheme, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  SelectChangeEvent,
  Stack
} from "@mui/material";
import { axiosApi } from "../../../interceptors/axios";
import { Result } from "../../../types/Result";

interface UserPreferenceStatistics {
  newUsersCount: number;
  oldUsersCount: number;
  totalUsersCount: number;
}

interface Profession {
  professionCode: string;
  name: string;
}

export default function UserPreferenceStats(): JSX.Element {
  const theme = useTheme();
  const [stats, setStats] = React.useState<UserPreferenceStatistics | null>(null);
  const [professions, setProfessions] = React.useState<Profession[]>([]);
  const [selectedProfession, setSelectedProfession] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(true);
  const [statsLoading, setStatsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  // Fetch professions on mount
  React.useEffect(() => {
    const fetchProfessions = async () => {
      try {
        const response = await axiosApi.get<Result<Profession[]>>("api/professions");
        if (response.data && response.data.isSuccess && response.data.data) {
          setProfessions(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching professions:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfessions();
  }, []);

  // Fetch stats when selectedProfession changes
  React.useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      setError(null);
      try {
        const url = `api/userPreferences/statistics${selectedProfession ? `?professionCode=${selectedProfession}` : ""}`;
        const response = await axiosApi.get<Result<UserPreferenceStatistics>>(url);
        if (response.data && response.data.isSuccess && response.data.data) {
          setStats(response.data.data);
        } else {
          setError("Veriler alınamadı");
        }
      } catch (err) {
        console.error("Error fetching statistics:", err);
        setError("Sunucu hatası");
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [selectedProfession]);

  const handleProfessionChange = (event: SelectChangeEvent) => {
    setSelectedProfession(event.target.value);
  };

  if (loading) {
    return (
      <Card variant="outlined" sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 250 }}>
        <CircularProgress size={24} />
      </Card>
    );
  }

  const chartData = stats ? [
    { id: 0, value: stats.newUsersCount, label: "Yeni Kayıt", color: theme.palette.success.main },
    { id: 1, value: stats.oldUsersCount, label: "Eski Kayıt", color: theme.palette.primary.main },
  ] : [];

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography component="h2" variant="subtitle2" color="primary">
            Atama Tercihi Yapan Kullanıcı Dağılımı
          </Typography>
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="profession-select-label">Branş Seçin</InputLabel>
            <Select
              labelId="profession-select-label"
              id="profession-select"
              value={selectedProfession}
              label="Branş Seçin"
              onChange={handleProfessionChange}
            >
              <MenuItem value="">
                <em>Varsayılan (Aktif)</em>
              </MenuItem>
              {professions.map((prof) => (
                <MenuItem key={prof.professionCode} value={prof.professionCode}>
                  {prof.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Box sx={{ height: 220, width: "100%", display: 'flex', justifyContent: 'center', position: 'relative' }}>
          {statsLoading && (
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1, bgcolor: 'rgba(255,255,255,0.7)', p: 1, borderRadius: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          
          {error ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
               <Typography variant="body2" color="error">{error}</Typography>
            </Box>
          ) : stats && stats.totalUsersCount > 0 ? (
            <PieChart
              series={[
                {
                  data: chartData,
                  highlightScope: { fade: "global", highlight: "item" },
                  faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                  innerRadius: 60,
                  outerRadius: 100,
                  paddingAngle: 2,
                  cornerRadius: 4,
                },
              ]}
              height={220}
              slotProps={{
                legend: {
                  position: { vertical: 'bottom', horizontal: 'center' },
                },
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Atama tercihi yapan kullanıcı bulunamadı
              </Typography>
            </Box>
          )}
        </Box>
        
        {stats && stats.totalUsersCount > 0 && (
          <Box sx={{ mt: 1, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Toplam {stats.totalUsersCount} kullanıcı tercih yaptı
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
